import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr, fmtIST } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import { Plus, FilePdf, Truck, Trash, FileArrowUp } from "@phosphor-icons/react";

export default function Dispatches() {
  const [list, setList] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const nav = useNavigate();

  const load = async () => {
    const r = await api.get("/warehouse/dispatches");
    setList(r.data);
  };
  useEffect(() => { load(); }, []);

  const openPdf = async (id) => {
    const token = localStorage.getItem("pos_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse/dispatches/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const remove = async (d) => {
    const inwarded = d.status === "inwarded";
    const msg = inwarded
      ? `Delete Delivery Challan ${d.invoice_no}?\n\nThis challan was already INWARDED at ${d.store_snapshot?.name}. Deleting it will REVERSE the received stock (${(d.items || []).reduce((a, i) => a + (i.qty || 0), 0)} units) out of the store inventory.\n\nThis cannot be undone.`
      : `Delete Delivery Challan ${d.invoice_no}?\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeleting(d.id);
    try {
      const r = await api.delete(`/warehouse/dispatches/${d.id}`);
      if (r.data.reversed_units) alert(`Challan deleted. ${r.data.reversed_units} units reversed from store stock.`);
      load();
    } catch (e) {
      alert("Delete failed: " + (e.response?.data?.detail || e.message));
    } finally { setDeleting(null); }
  };

  return (
    <Shell>
      <PageHeader
        title="Delivery Challans"
        subtitle="Goods sent from Main Warehouse to stores under a GST Delivery Challan (with e-way bill when required)."
        testId="dispatches-header"
        actions={<>
          <button onClick={() => nav("/warehouse/dispatches/import")} data-testid="import-challan-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-bold rounded-md"><FileArrowUp size={16} weight="bold" />Upload Tally PDF</button>
          <button onClick={() => nav("/warehouse/dispatches/new")} data-testid="new-dispatch-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md"><Plus size={16} weight="bold" />New Delivery Challan</button>
        </>}
      />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="dispatches-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Challan #</th>
                <th className="text-left px-4 py-3 font-semibold">Store</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                <th className="text-left px-4 py-3 font-semibold">Items</th>
                <th className="text-right px-4 py-3 font-semibold">Grand Total</th>
                <th className="text-left px-4 py-3 font-semibold">E-Way Bill</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-400">No delivery challans yet. Create one manually or upload a Tally PDF.</td></tr>}
              {list.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`challan-row-${d.invoice_no}`}>
                  <td className="px-4 py-3 font-mono-num font-semibold">{d.invoice_no}</td>
                  <td className="px-4 py-3">{d.store_snapshot?.name}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{fmtIST(d.created_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    {d.source === "pdf_import"
                      ? <span className="inline-flex flex-col"><span className="font-semibold text-slate-700">Tally PDF</span><span className="font-mono-num text-[10px] text-slate-500">{d.source_doc_no}</span></span>
                      : <span className="text-slate-500">Manual</span>}
                  </td>
                  <td className="px-4 py-3 font-mono-num">{d.items?.length || 0}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold">{inr(d.grand_total)}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{d.eway_bill_no || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${d.status === 'inwarded' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      <Truck size={10} weight="bold" /> {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button onClick={() => openPdf(d.id)} data-testid={`dispatch-pdf-${d.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"><FilePdf size={14} /> PDF</button>
                      {isAdmin && (
                        <button onClick={() => remove(d)} disabled={deleting === d.id} data-testid={`dispatch-delete-${d.invoice_no}`} title="Delete challan (admin)" className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"><Trash size={14} /> Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
